import * as React from 'preact/compat'
import { SeriesSchemeState } from './state'
import type { Coords } from './SeriesSchemeRenderer'
import { signal } from '@preact/signals'
import type { CSSProperties } from 'preact/compat'
import cn from 'classnames'
import styles from '../../../css/game-series-scheme.pcss.json'
import { wait } from '../../common/helpers'

interface PropTypes {
  sourceElement: HTMLElement,
  targetElement: HTMLElement,
  onlyDown?: boolean,
  highlighted?: boolean
}

export default class Line extends React.Component<PropTypes, any> {
  static contextType = SeriesSchemeState
  declare context: React.ContextType<typeof SeriesSchemeState>

  debug = false
  containerBBox: DOMRect
  sourceBBox: DOMRect
  targetBBox: DOMRect
  endsCoords: {
    source: Coords,
    target: Coords
  }
  pathCoords: Array<Coords> = []
  pathString = signal<string>('')
  avoidanceCounter = 0
  gap = 13
  hidden = signal<Boolean>(false)
  changedHighlight = false

  constructor(props: PropTypes, context: React.ContextType<typeof SeriesSchemeState>) {
    super(props, context)
  }

  componentDidMount() {
    this.containerBBox = this.context.container.value.getBoundingClientRect()
    this.sourceBBox = this.props.sourceElement.getBoundingClientRect()
    this.targetBBox = this.props.targetElement.getBoundingClientRect()
    this.endsCoords = this.getEndsCoords()
    this.calculatePath()
    this.makePathString()
  }

  componentWillUpdate(nextProps: Readonly<PropTypes>) {
    if (nextProps.highlighted !== this.props.highlighted) {
      this.changedHighlight = true
    }
  }

  componentDidUpdate() {
    if (this.changedHighlight) {
      this.hidden.value = true
      this.changedHighlight = false
      wait(10).then(() => {
        this.hidden.value = false
      })
    }
  }

  getEndsCoords = () => {
    const sourceX = this.sourceBBox.x - this.containerBBox.x
    const sourceY = this.sourceBBox.y - this.containerBBox.y
    const targetX = this.targetBBox.x - this.containerBBox.x
    const targetY = this.targetBBox.y - this.containerBBox.y
    let sourceCoords: Coords = {
      x: sourceX + this.sourceBBox.width / 2,
      y: sourceY + this.sourceBBox.height + this.gap
    }
    let targetCoords: Coords = {
      x: targetX + this.targetBBox.width / 2,
      y: targetY - this.gap
    }
    const deltaX = sourceCoords.x - targetCoords.x
    const deltaY = sourceCoords.y - targetCoords.y

    if (Math.abs(deltaX) > this.sourceBBox.width && !this.props.onlyDown) {
      if (deltaX > 0) {
        sourceCoords = {
          x: sourceX - this.gap,
          y: sourceY + this.sourceBBox.height / 2
        }
        targetCoords = {
          x: targetX + this.targetBBox.width + this.gap,
          y: targetY + this.targetBBox.height / 2
        }
      } else {
        sourceCoords = {
          x: sourceX + this.sourceBBox.width + this.gap,
          y: sourceY + this.sourceBBox.height / 2,
        }
        targetCoords = {
          x: targetX - this.gap,
          y: targetY + this.targetBBox.height / 2,
        }
      }
    } else if (deltaY > 0) {
      sourceCoords = {
        x: sourceX + this.sourceBBox.width / 2,
        y: sourceY - this.gap,
      }
      targetCoords = {
        x: targetX + this.targetBBox.width / 2,
        y: targetY + this.targetBBox.height + this.gap,
      }
    }

    if (!this.props.onlyDown
      && (
        sourceCoords.x > targetCoords.x
        || sourceCoords.y > targetCoords.y
      )
    ) {
      return {
        source: targetCoords,
        target: sourceCoords
      }
    }

    return {
      source: sourceCoords,
      target: targetCoords,
    }
  }

  testCollision = (coord1: Coords, coord2: Coords): HTMLElement & {
    precomputedBBox: DOMRect
  } | false => {
    const deltaX = coord2.x - coord1.x
    const deltaY = coord2.y - coord1.y
    const largest = Math.max(Math.abs(deltaX), Math.abs(deltaY))
    const numOfTests = Math.round(largest / 100)
    const percentX = deltaX / numOfTests
    const percentY = deltaY / numOfTests
    for (let testNum = 1; testNum <= numOfTests; testNum++) {
      const testPoint: Coords = {
        x: this.containerBBox.x + coord1.x + percentX * testNum,
        y: this.containerBBox.y + coord1.y + percentY * testNum,
      }

      if (!this.context.gameElements.value) {
        return false
      }

      for (let id in this.context.gameElements.value) {
        const element = this.context.gameElements.value[id]
        if (
          element === this.props.sourceElement ||
          element === this.props.targetElement
        ) {
          continue
        }
        if (!element.precomputedBBox) {
          element.precomputedBBox = element.getBoundingClientRect()
        }
        const bBox = element.precomputedBBox
        if (
          bBox &&
          testPoint.x > bBox.x &&
          testPoint.x < bBox.x + bBox.width &&
          testPoint.y > bBox.y &&
          testPoint.y < bBox.y + bBox.height
        ) {
          this.renderDebugPoint(testPoint, "red", true)
          return element
        }
      }
      this.renderDebugPoint(testPoint, "blue", true)
    }
    return false
  }

  calculatePath = () => {
    const { source, target } = this.endsCoords
    this.pathCoords.push(source)

    // Not straight horizontal or vertical line
    if (source.x !== target.x && source.y !== target.y) {
      if (this.props.onlyDown) {
        this.pathCoords.push({
          x: source.x,
          y: source.y + 35,
        })
        this.pathCoords.push({
          x: target.x,
          y: source.y + 35,
        })
      } else {
        const deltaX = target.x - source.x
        this.pathCoords.push({
          x: source.x + deltaX / 2,
          y: source.y,
        })
        this.pathCoords.push({
          x: source.x + deltaX / 2,
          y: target.y,
        })
      }
    }

    this.pathCoords.push(target)
    if (!this.props.onlyDown) {
      this.collisionAvoidance()
    }
  }

  renderDebugPoint = (coords: Coords, color: string, removeBBox?: boolean) => {
    if (!this.debug) {
      return
    }
    const div = document.createElement("div");
    let realCoords = { ...coords }
    if (removeBBox) {
      realCoords.x -= this.containerBBox.x
      realCoords.y -= this.containerBBox.y
    }
    Object.assign(div.style, {
      position: "absolute",
      top: `${realCoords.y}px`,
      left: `${realCoords.x}px`,
      width: "5px",
      height: "5px",
      background: color,
      borderRadius: "5px",
      zIndex: 2
    });
    div.setAttribute("source", `${this.props.sourceElement.id}`);
    div.setAttribute("target", `${this.props.targetElement.id}`);
    this.context.container.value?.appendChild(div);
  };

  horizontalAvoidance = (
    triedObviousSolution: boolean = false
  ) => {
    let hadMidSection = false
    let isReverseX = false
    let isReverseY = false
    for (let i in this.pathCoords) {
      const index = parseInt(i)
      const end = this.pathCoords[index + 1]
      const start = this.pathCoords[index]
      this.renderDebugPoint(start, "green")

      if (!end) {
        return
      }

      let isMidSection = start.x === end.x
      if (isMidSection) {
        hadMidSection = true
        isReverseY = (start.y - end.y) > 0
      }
      if (!isMidSection) {
        isReverseX = (start.x - end.x) > 0
      }

      const collision = this.testCollision(start, end)
      if (collision && collision.precomputedBBox) {
        const startOfTheBox: Coords = {
          x: collision.precomputedBBox.x - this.containerBBox.x,
          y: collision.precomputedBBox.y - this.containerBBox.y,
        }
        const endOfTheBox: Coords = {
          x: startOfTheBox.x + collision.precomputedBBox.width,
          y: startOfTheBox.y + collision.precomputedBBox.height,
        }
        let indexToDeleteFrom = index + 1
        if (!isMidSection && hadMidSection) {
          indexToDeleteFrom = index
          isMidSection = true
        }
        this.pathCoords.splice(indexToDeleteFrom, this.pathCoords.length - (indexToDeleteFrom - 2))
        let skipWalkBack = false
        let lastOffAxis = start.y

        if (!isMidSection) {
          // walk-around avoidance type
          let xLimit = startOfTheBox.x - 40
          if (isReverseX) {
            xLimit = endOfTheBox.x + 40
          }
          this.pathCoords.push({
            x: xLimit,
            y: start.y,
          })
          let walkAroundOffAxis = startOfTheBox.y - 40
          if (this.endsCoords.target.y < walkAroundOffAxis) {
            walkAroundOffAxis = this.endsCoords.target.y
            skipWalkBack = true
          }
          if (this.targetBBox.y > collision.precomputedBBox.y) {
            walkAroundOffAxis = endOfTheBox.y + 40
          }
          if (this.endsCoords.target.y > walkAroundOffAxis) {
            walkAroundOffAxis = this.endsCoords.target.y
            skipWalkBack = true
          }

          this.pathCoords.push({
            x: xLimit,
            y: walkAroundOffAxis,
          })
          lastOffAxis = walkAroundOffAxis
        }

        if (isMidSection) {
          if (!triedObviousSolution) {
            let xStop = this.endsCoords.target.x - 27
            if (isReverseX) {
              xStop = this.endsCoords.target.x + 27
            }
            this.pathCoords.push({
              x: xStop,
              y: this.endsCoords.source.y
            })
            this.pathCoords.push({
              x: xStop,
              y: this.endsCoords.target.y
            })
            skipWalkBack = true;
          } else {
            let yStop = startOfTheBox.y - 40
            if (isReverseY) {
              yStop = endOfTheBox.y + 40
            }
            this.pathCoords.push({
              x: start.x,
              y: yStop
            })
            lastOffAxis = yStop
          }
        }

        // continuation avoidance type
        if (!skipWalkBack) {
          let xStop = this.endsCoords.target.x - 27
          if (isReverseX) {
            xStop = this.endsCoords.target.x + 27
          }
          this.pathCoords.push({
            x: xStop,
            y: lastOffAxis,
          })
          this.pathCoords.push({
            x: xStop,
            y: this.endsCoords.target.y
          })
        }

        this.renderDebugPoint(this.endsCoords.target, "green")
        this.pathCoords.push(this.endsCoords.target)
        this.collisionAvoidance(true)
        break
      }
    }
  }

  verticalAvoidance = () => {
    for (let i in this.pathCoords) {
      const index = parseInt(i)
      const end = this.pathCoords[index + 1]
      const start = this.pathCoords[index]

      if (!end) {
        return
      }

      const collision = this.testCollision(start, end)
      if (collision && collision.precomputedBBox) {
        const sourceX = this.sourceBBox.x - this.containerBBox.x
        const sourceY = this.sourceBBox.y - this.containerBBox.y
        const targetY = this.targetBBox.y - this.containerBBox.y

        this.pathCoords = [
          {
            x: sourceX - this.gap,
            y: sourceY + this.sourceBBox.height / 2
          },
          {
            x: sourceX - 40,
            y: sourceY + this.sourceBBox.height / 2
          },
          {
            x: sourceX - 40,
            y: targetY + this.targetBBox.height / 2
          },
          {
            x: sourceX - this.gap,
            y: targetY + this.targetBBox.height / 2
          }
        ]
        break
      }
    }
  }

  collisionAvoidance = (triedObviousSolution: boolean = false) => {
    this.avoidanceCounter++
    if (this.avoidanceCounter > 10) {
      return
    }
    if (this.pathCoords[0].x === this.pathCoords[1].x) {
      this.verticalAvoidance()
      return
    }
    this.horizontalAvoidance(triedObviousSolution)
  }

  makePathString = () => {
    let pathCommands: string[] = []
    const arcRadius = 16;
    pathCommands.push(`M${this.pathCoords[0].x} ${this.pathCoords[0].y}`)
    this.pathCoords.forEach((current, key) => {
      const prev = this.pathCoords[key - 1]
      const next = this.pathCoords[key + 1]
      if (!prev && !next) {
        return
      }
      if (prev && prev.x === current.x) {
        if (next && next.y === current.y) {
          let flip = 0
          const hDirection = Math.sign(next.x - current.x)
          const vDirection = Math.sign(current.y - prev.y)
          if (hDirection > 0 && vDirection < 0
            || hDirection < 0 && vDirection > 0
          ) {
            flip = 1
          }
          pathCommands.push(`V${current.y + (arcRadius * -vDirection)}`)
          pathCommands.push(`A${arcRadius} ${arcRadius} 0 0 ${flip} ${current.x + (arcRadius * hDirection)} ${current.y}`)
          return
        }
        pathCommands.push(`V${current.y}`)
        return
      }
      if (prev && prev.y === current.y) {
        if (next && next.x === current.x) {
          let flip = 0
          const hDirection = Math.sign(current.x - prev.x)
          const vDirection = Math.sign(next.y - current.y)
          if (hDirection > 0 && vDirection > 0
            || hDirection < 0 && vDirection < 0
          ) {
            flip = 1
          }
          pathCommands.push(`H${current.x + (arcRadius * -hDirection)}`)
          pathCommands.push(`A${arcRadius} ${arcRadius} 0 0 ${flip} ${current.x} ${current.y + (arcRadius * vDirection)}`)
          return
        }
        pathCommands.push(`H${current.x}`)
        return
      }
      pathCommands.push(`L${current.x} ${current.y}`)
    })
    this.pathString.value = pathCommands.join('')
  }

  render() {
    const { highlighted } = this.props
    if (this.pathString.value === '') {
      return null
    }

    const style: CSSProperties = {}

    // Хак для Safari
    if (this.hidden.value) {
      style.display = 'none'
    }

    return <path
      className={cn({
        [styles['line']]: true,
        [styles['line--highlighted']]: highlighted
      })}
      d={this.pathString.value}
      style={style}
    />
  }
}
